// MARK://dhk8/pos/pages/ReservationsPage
// Lane #164 — calendario de reservas standalone (no drawer en /floor).
//
// Vistas: 1 día | 3 días | 5 días | 7 días | mes
// El calendario muestra slots cada 30 min (horario operativo) y cards por
// reserva. Navegación con ‹ › o tap directo a una fecha.
//
// Modal nueva reserva usa @dockethub/ui Dialog + Input + Select + Button (mismo
// look-and-feel que el modal "Nueva mesa" del FloorEditor).
//
// Útil para planeación (semana entrante) y operación (qué hay hoy).

import { useEffect, useMemo, useState } from 'react'
import {
  Plus, ChevronLeft, ChevronRight, RefreshCw, Phone, Mail, Baby, Accessibility,
  AlertTriangle, Gift, X, Check, Users,
} from 'lucide-react'
import { Button, Dialog, Input, Select } from '@dockethub/ui'

import {
  listReservations,
  createReservation,
  updateReservation,
  cancelReservation,
  getFloorState,
  type ReservationDto,
  type CreateReservationInput,
} from '../lib/clients/floor.js'
import { useConfirm } from '../components/ConfirmDialog.js'
import { SharePublicLink } from '../features/SharePublicLink.js'
import { businessDayOf } from '../lib/business-day.js'

type RangeMode = 1 | 3 | 5 | 7 | 30

const POLL_MS = 60_000

function todayISO(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function isoAdd(base: string, days: number): string {
  const [y, m, d] = base.split('-').map(Number)
  const dt = new Date(y ?? 2026, (m ?? 1) - 1, d ?? 1)
  dt.setDate(dt.getDate() + days)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
}
function fmtDayLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00`)
  return d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' })
}
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
}
function statusTone(s: ReservationDto['status']): string {
  switch (s) {
    case 'CONFIRMED': return 'tone-info'
    case 'ARRIVED':   return 'tone-warn'
    case 'SEATED':    return 'tone-ok'
    case 'CANCELLED':
    case 'NO_SHOW':   return 'tone-mute'
  }
}
function statusLabel(s: ReservationDto['status']): string {
  switch (s) {
    case 'CONFIRMED': return 'Confirmada'
    case 'ARRIVED':   return 'Llegó'
    case 'SEATED':    return 'Sentada'
    case 'CANCELLED': return 'Cancelada'
    case 'NO_SHOW':   return 'No-show'
  }
}

const OCCASION_LABEL: Record<NonNullable<ReservationDto['occasion']>, string> = {
  birthday: '🎂 Cumpleaños',
  anniversary: '💍 Aniversario',
  date: '❤️ Cita',
  business: '💼 Negocios',
  other: 'Otro',
}

export default function ReservationsPage() {
  const confirm = useConfirm()
  const [mode, setMode] = useState<RangeMode>(3)
  const [anchorDate, setAnchorDate] = useState<string>(todayISO()) // primer día visible
  const [items, setItems] = useState<ReservationDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState<{ date: string; time?: string } | null>(null)
  const [editing, setEditing] = useState<ReservationDto | null>(null)

  // Días visibles para la vista actual.
  const days = useMemo(() => {
    return Array.from({ length: mode }, (_, i) => isoAdd(anchorDate, i))
  }, [anchorDate, mode])

  // Rango from/to para query.
  const queryRange = useMemo(() => {
    const from = new Date(`${days[0]}T00:00:00`).toISOString()
    const last = days[days.length - 1] ?? days[0]
    const to = new Date(`${last}T23:59:59`).toISOString()
    return { from, to }
  }, [days])

  async function reload() {
    try {
      setError(null)
      setLoading(true)
      const r = await listReservations({ from: queryRange.from, to: queryRange.to })
      r.sort((a, b) => a.reservedAt.localeCompare(b.reservedAt))
      setItems(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    const iv = setInterval(() => void reload(), POLL_MS)
    return () => clearInterval(iv)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryRange.from, queryRange.to])

  // Agrupar por día (clave YYYY-MM-DD).
  const byDay = useMemo(() => {
    const m = new Map<string, ReservationDto[]>()
    for (const d of days) m.set(d, [])
    for (const r of items) {
      // El día del NEGOCIO, no el UTC: `reservedAt.slice(0,10)` mandaba la
      // cena de las 21:00 al día siguiente. Ver lib/business-day.ts.
      const k = businessDayOf(r)
      if (m.has(k)) m.get(k)!.push(r)
    }
    return m
  }, [items, days])

  function shift(dir: -1 | 1) {
    setAnchorDate((d) => isoAdd(d, dir * (mode === 30 ? 30 : Math.max(1, mode))))
  }

  async function handleStatus(r: ReservationDto, status: ReservationDto['status']) {
    await updateReservation(r.id, { status })
    await reload()
  }
  async function handleCancel(r: ReservationDto) {
    if (!(await confirm({ title: 'Cancelar reserva', message: `¿Cancelar reserva de ${r.name}?`, confirmLabel: 'Cancelar reserva', cancelLabel: 'No', tone: 'danger' }))) return
    await cancelReservation(r.id)
    await reload()
  }

  return (
    <div className="dhk8-resv-page">
      <header className="dhk8-resv-page__head">
        <div>
          <h1 className="dhk8-resv-page__title">Reservas</h1>
          <p className="dhk8-resv-page__sub">Vista de calendario · planeación + operación</p>
        </div>
        <div className="dhk8-resv-page__actions">
          <SharePublicLink kind="reservar" label="Compartir reservas" />
          <Button variant="ghost" size="sm" leading={<RefreshCw size={14} />} onClick={() => void reload()}>Recargar</Button>
          <Button variant="primary" size="sm" leading={<Plus size={14} />} onClick={() => setCreating({ date: anchorDate })}>Nueva reserva</Button>
        </div>
      </header>

      {/* Toolbar: nav + rango + jump to date */}
      <div className="dhk8-resv-page__toolbar">
        <div className="dhk8-resv-page__nav">
          <Button variant="ghost" size="sm" onClick={() => shift(-1)} title="Anterior"><ChevronLeft size={14} /></Button>
          <Button variant="ghost" size="sm" onClick={() => setAnchorDate(todayISO())}>Hoy</Button>
          <Button variant="ghost" size="sm" onClick={() => shift(1)} title="Siguiente"><ChevronRight size={14} /></Button>
          <input
            type="date"
            value={anchorDate}
            onChange={(e) => setAnchorDate(e.target.value)}
            className="dhk8-resv-page__jump"
          />
        </div>
        <div className="dhk8-resv-page__modes">
          {([1, 3, 5, 7, 30] as const).map((n) => (
            <button
              key={n}
              type="button"
              className={`dhk8-resv-page__mode${mode === n ? ' is-active' : ''}`}
              onClick={() => setMode(n)}
            >
              {n === 30 ? 'Mes' : `${n} día${n > 1 ? 's' : ''}`}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="dhk8-resv-page__err" role="alert">{error}</div>}

      {/* Grid de columnas por día */}
      <div className={`dhk8-resv-grid${mode === 30 ? ' is-month' : ''}`} style={{ gridTemplateColumns: `repeat(${Math.min(mode, mode === 30 ? 7 : mode)}, minmax(0, 1fr))` }}>
        {mode === 30 ? (
          // Vista mes: 30 días en filas de 7
          days.map((d) => (
            <DayColumn
              key={d}
              date={d}
              reservations={byDay.get(d) ?? []}
              compact
              onAdd={(time) => setCreating({ date: d, ...(time ? { time } : {}) })}
              onEdit={(r) => setEditing(r)}
              onStatus={handleStatus}
              onCancel={handleCancel}
              loading={loading}
            />
          ))
        ) : (
          days.map((d) => (
            <DayColumn
              key={d}
              date={d}
              reservations={byDay.get(d) ?? []}
              onAdd={(time) => setCreating({ date: d, ...(time ? { time } : {}) })}
              onEdit={(r) => setEditing(r)}
              onStatus={handleStatus}
              onCancel={handleCancel}
              loading={loading}
            />
          ))
        )}
      </div>

      {creating && (
        <ReservationDialog
          defaultDate={creating.date}
          {...(creating.time !== undefined ? { defaultTime: creating.time } : {})}
          onClose={() => setCreating(null)}
          onSaved={() => { setCreating(null); void reload() }}
        />
      )}
      {editing && (
        <ReservationDialog
          edit={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void reload() }}
        />
      )}

      <style>{styles}</style>
    </div>
  )
}

// ── DayColumn ───────────────────────────────────────────────────────────────

function DayColumn({ date, reservations, compact, loading, onAdd, onEdit, onStatus, onCancel }: {
  date: string
  reservations: ReservationDto[]
  compact?: boolean
  loading?: boolean
  onAdd: (time?: string) => void
  onEdit: (r: ReservationDto) => void
  onStatus: (r: ReservationDto, status: ReservationDto['status']) => void | Promise<void>
  onCancel: (r: ReservationDto) => void | Promise<void>
}) {
  const isToday = date === todayISO()
  return (
    <div className={`dhk8-resv-col${isToday ? ' is-today' : ''}${compact ? ' is-compact' : ''}`}>
      <div className="dhk8-resv-col__head" onClick={() => onAdd()}>
        <span className="dhk8-resv-col__day-label">{fmtDayLabel(date)}</span>
        <span className="dhk8-resv-col__count">{reservations.length}</span>
      </div>
      <div className="dhk8-resv-col__list">
        {loading && reservations.length === 0 && <div className="dhk8-resv-col__empty">—</div>}
        {!loading && reservations.length === 0 && (
          <button type="button" className="dhk8-resv-col__add" onClick={() => onAdd()}>
            <Plus size={14} /> Agregar
          </button>
        )}
        {reservations.map((r) => (
          <article
            key={r.id}
            className={`dhk8-resv-card ${statusTone(r.status)}${compact ? ' is-compact' : ''}`}
            onClick={() => onEdit(r)}
          >
            <header className="dhk8-resv-card__head">
              <strong className="dhk8-resv-card__time">{fmtTime(r.reservedAt)}</strong>
              {r.reservationCode && <span className="dhk8-resv-card__code">{r.reservationCode}</span>}
            </header>
            <div className="dhk8-resv-card__body">
              <span className="dhk8-resv-card__name">{r.name}</span>
              <span className="dhk8-resv-card__party"><Users size={10} /> {r.partySize}</span>
            </div>
            {!compact && (
              <div className="dhk8-resv-card__pills">
                {r.occasion && <span className="dhk8-resv-card__pill">{OCCASION_LABEL[r.occasion]}</span>}
                {r.highChair && <span className="dhk8-resv-card__pill"><Baby size={9} /></span>}
                {r.wheelchair && <span className="dhk8-resv-card__pill"><Accessibility size={9} /></span>}
              </div>
            )}
            {!compact && r.allergies && (
              <div className="dhk8-resv-card__allergies">
                <AlertTriangle size={10} /> {r.allergies}
              </div>
            )}
            {!compact && (
              <footer className="dhk8-resv-card__footer">
                {r.status === 'CONFIRMED' && (
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); void onStatus(r, 'ARRIVED') }}>
                    <Check size={12} /> Llegó
                  </Button>
                )}
                {(r.status === 'CONFIRMED' || r.status === 'ARRIVED') && (
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); void onCancel(r) }}>
                    <X size={12} />
                  </Button>
                )}
                <span className={`dhk8-resv-card__badge ${statusTone(r.status)}`}>{statusLabel(r.status)}</span>
              </footer>
            )}
          </article>
        ))}
      </div>
    </div>
  )
}

// ── ReservationDialog (create + edit) — usa @dockethub/ui ─────────────────────────

function ReservationDialog({ defaultDate, defaultTime, edit, onClose, onSaved }: {
  defaultDate?: string
  defaultTime?: string
  edit?: ReservationDto
  onClose: () => void
  onSaved: () => void
}) {
  // Lane #165 — pre-asignar mesa al crear/editar la reserva. Cargamos las
  // mesas disponibles al abrir el dialog (no incluyen UNAVAILABLE).
  const [allTables, setAllTables] = useState<Array<{ id: string; code: string; capacity: number; status: string }>>([])
  useEffect(() => {
    let alive = true
    void getFloorState().then((s) => {
      if (alive) setAllTables(s.tables.filter((t) => t.status !== 'UNAVAILABLE'))
    }).catch(() => { /* ignore — selector solo no aparece */ })
    return () => { alive = false }
  }, [])
  const isEdit = !!edit
  const initialReservedAt = edit ? new Date(edit.reservedAt) : null
  const initialDate = edit && initialReservedAt
    ? `${initialReservedAt.getFullYear()}-${String(initialReservedAt.getMonth() + 1).padStart(2, '0')}-${String(initialReservedAt.getDate()).padStart(2, '0')}`
    : (defaultDate ?? todayISO())
  const initialTime = edit && initialReservedAt
    ? `${String(initialReservedAt.getHours()).padStart(2, '0')}:${String(initialReservedAt.getMinutes()).padStart(2, '0')}`
    : (defaultTime ?? '19:30')

  const [name, setName] = useState(edit?.name ?? '')
  const [phone, setPhone] = useState(edit?.phone ?? '')
  const [email, setEmail] = useState(edit?.email ?? '')
  const [partySize, setPartySize] = useState(edit?.partySize ?? 2)
  const [date, setDate] = useState(initialDate)
  const [time, setTime] = useState(initialTime)
  const [durationMin, setDurationMin] = useState(edit?.durationMin ?? 90)
  const [highChair, setHighChair] = useState(edit?.highChair ?? false)
  const [wheelchair, setWheelchair] = useState(edit?.wheelchair ?? false)
  const [occasion, setOccasion] = useState<'' | NonNullable<ReservationDto['occasion']>>(edit?.occasion ?? '')
  const [source, setSource] = useState<'' | NonNullable<ReservationDto['source']>>(edit?.source ?? (isEdit ? '' : 'walk_in'))
  const [allergies, setAllergies] = useState(edit?.allergies ?? '')
  const [notes, setNotes] = useState(edit?.notes ?? '')
  // Lane #165 — selector de mesa pre-asignada (opcional).
  const [tableId, setTableId] = useState<string | null>(edit?.tableId ?? null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleSubmit() {
    if (!name.trim()) { setErr('Nombre requerido'); return }
    setBusy(true); setErr(null)
    try {
      const [hh, mm] = time.split(':').map(Number)
      const [yy, mo, dd] = date.split('-').map(Number)
      const d = new Date(yy ?? 2026, (mo ?? 1) - 1, dd ?? 1, hh ?? 19, mm ?? 0, 0, 0)
      if (Number.isNaN(d.getTime())) { setErr('Fecha/hora inválida'); setBusy(false); return }

      if (isEdit && edit) {
        const update: Parameters<typeof updateReservation>[1] = {
          name: name.trim(),
          partySize,
          reservedAt: d.toISOString(),
          durationMin,
          phone: phone.trim() || null,
          email: email.trim() || null,
          notes: notes.trim() || null,
          allergies: allergies.trim() || null,
          highChair,
          wheelchair,
          tableId, // Lane #165 — null = sin pre-asignar, string = mesa pre-asignada
        }
        if (occasion) update.occasion = occasion
        if (source) update.source = source
        await updateReservation(edit.id, update)
      } else {
        const input: CreateReservationInput = {
          name: name.trim(),
          partySize,
          reservedAt: d.toISOString(),
          durationMin,
          highChair,
          wheelchair,
        }
        if (phone.trim()) input.phone = phone.trim()
        if (email.trim()) input.email = email.trim()
        if (notes.trim()) input.notes = notes.trim()
        if (allergies.trim()) input.allergies = allergies.trim()
        if (occasion) input.occasion = occasion
        if (source) input.source = source
        if (tableId) input.tableId = tableId
        await createReservation(input)
      }
      onSaved()
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error al guardar')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      title={isEdit ? `Reserva ${edit?.reservationCode ?? ''}` : 'Nueva reserva'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button variant="primary" onClick={() => void handleSubmit()} disabled={busy || !name.trim()}>
            {busy ? 'Guardando…' : isEdit ? 'Guardar' : 'Crear'}
          </Button>
        </>
      }
    >
      <label className="dhk8-resv-field">
        <span>Nombre</span>
        <Input value={name} onChange={(e) => setName(e.currentTarget.value)} autoFocus />
      </label>

      <div className="dhk8-resv-field-row">
        <label className="dhk8-resv-field">
          <span>Teléfono <Phone size={10} /></span>
          <Input value={phone} onChange={(e) => setPhone(e.currentTarget.value)} placeholder="+52..." />
        </label>
        <label className="dhk8-resv-field">
          <span>Email <Mail size={10} /></span>
          <Input type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} placeholder="cliente@..." />
        </label>
      </div>

      <div className="dhk8-resv-field-row">
        <label className="dhk8-resv-field">
          <span>Fecha</span>
          <Input type="date" value={date} onChange={(e) => setDate(e.currentTarget.value)} min={todayISO()} />
        </label>
        <label className="dhk8-resv-field">
          <span>Hora</span>
          <Input type="time" value={time} onChange={(e) => setTime(e.currentTarget.value)} />
        </label>
        <label className="dhk8-resv-field">
          <span>Duración (min)</span>
          <Input
            type="number"
            value={String(durationMin)}
            onChange={(e) => setDurationMin(Math.max(15, Number(e.currentTarget.value) || 90))}
          />
        </label>
      </div>

      <div className="dhk8-resv-field-row">
        <label className="dhk8-resv-field">
          <span>Personas</span>
          <Select value={String(partySize)} onChange={(e) => setPartySize(Number(e.currentTarget.value))}>
            {Array.from({ length: 30 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={String(n)}>{n}</option>
            ))}
          </Select>
        </label>
        <label className="dhk8-resv-field">
          <span><Gift size={10} /> Ocasión</span>
          <Select value={occasion} onChange={(e) => setOccasion(e.currentTarget.value as typeof occasion)}>
            <option value="">—</option>
            <option value="birthday">Cumpleaños</option>
            <option value="anniversary">Aniversario</option>
            <option value="date">Cita</option>
            <option value="business">Negocios</option>
            <option value="other">Otro</option>
          </Select>
        </label>
        <label className="dhk8-resv-field">
          <span>Origen</span>
          <Select value={source} onChange={(e) => setSource(e.currentTarget.value as typeof source)}>
            <option value="walk_in">Walk-in</option>
            <option value="phone">Llamada</option>
            <option value="web">Web</option>
            <option value="instagram">Instagram</option>
            <option value="recommended">Recomendado</option>
            <option value="other">Otro</option>
          </Select>
        </label>
      </div>

      <div className="dhk8-resv-checks">
        <label className="dhk8-resv-check">
          <input type="checkbox" checked={highChair} onChange={(e) => setHighChair(e.target.checked)} />
          <Baby size={14} /> Silla para bebé
        </label>
        <label className="dhk8-resv-check">
          <input type="checkbox" checked={wheelchair} onChange={(e) => setWheelchair(e.target.checked)} />
          <Accessibility size={14} /> Accesibilidad
        </label>
      </div>

      {/* Lane #165 — pre-asignar mesa al hacer la reserva (opcional). */}
      {allTables.length > 0 && (
        <div className="dhk8-resv-field">
          <span>Pre-asignar mesa (opcional)</span>
          <div className="dhk8-resv-tables">
            <button
              type="button"
              className={`dhk8-resv-table${tableId === null ? ' is-active' : ''}`}
              onClick={() => setTableId(null)}
            >Sin pre-asignar</button>
            {allTables.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`dhk8-resv-table${tableId === t.id ? ' is-active' : ''}`}
                onClick={() => setTableId(t.id)}
              >
                <span className="dhk8-resv-table-code">{t.code}</span>
                <span className="dhk8-resv-table-cap">{t.capacity}p</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <label className="dhk8-resv-field">
        <span><AlertTriangle size={10} /> Alergias / restricciones</span>
        <Input value={allergies} onChange={(e) => setAllergies(e.currentTarget.value)} placeholder="Maní, gluten, vegetariano..." />
      </label>

      <label className="dhk8-resv-field">
        <span>Notas</span>
        <Input value={notes} onChange={(e) => setNotes(e.currentTarget.value)} placeholder="Mesa cerca ventana, sorpresa..." />
      </label>

      {err && <div className="dhk8-resv-page__err" role="alert">{err}</div>}
    </Dialog>
  )
}

const styles = `
  .dhk8-resv-page { display: flex; flex-direction: column; gap: 12px; padding: 12px; min-height: 100%; }
  .dhk8-resv-page__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
  .dhk8-resv-page__title { font-size: 20px; font-weight: 600; margin: 0; color: var(--pos-fg, #E8EFE9); font-family: var(--font-display, sans-serif); }
  .dhk8-resv-page__sub { font-size: 12px; color: var(--pos-fg-tertiary, #7E8C82); margin: 4px 0 0; }
  .dhk8-resv-page__actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .dhk8-resv-page__err { padding: 8px 12px; background: rgba(229,72,77,0.15); border: 1px solid var(--pos-danger, #E5484D); color: var(--pos-danger, #E5484D); font-size: 13px; border-radius: var(--pos-radius-md, 8px); }

  /* ── Toolbar ─────────────────────────────────────────────────────────── */
  .dhk8-resv-page__toolbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; padding: 6px 0; border-bottom: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.14)); }
  .dhk8-resv-page__nav { display: flex; gap: 4px; align-items: center; }
  .dhk8-resv-page__jump { padding: 6px 8px; background: var(--pos-surface, #161C18); border: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.14)); border-radius: var(--pos-radius-md, 8px); color: var(--pos-fg, #E8EFE9); font-family: inherit; font-size: 12px; margin-left: 4px; }
  .dhk8-resv-page__modes { display: flex; gap: 2px; }
  .dhk8-resv-page__mode { padding: 6px 12px; background: transparent; border: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.14)); color: var(--pos-fg-secondary, #B6BFB8); font-family: inherit; font-size: 12px; cursor: pointer; border-radius: var(--pos-radius-md, 8px); transition: background 120ms ease; }
  .dhk8-resv-page__mode:hover { background: var(--pos-elevated, #1E2520); }
  .dhk8-resv-page__mode.is-active { background: var(--pos-tint, #1F8B3A); color: var(--pos-bg, #000); border-color: var(--pos-tint, #1F8B3A); font-weight: 600; }

  /* ── Grid ────────────────────────────────────────────────────────────── */
  .dhk8-resv-grid { display: grid; gap: 8px; min-height: 400px; }
  .dhk8-resv-grid.is-month { grid-template-rows: repeat(auto-fill, minmax(120px, 1fr)); }
  .dhk8-resv-col { display: flex; flex-direction: column; gap: 6px; background: var(--pos-elevated, #1E2520); border: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.14)); border-radius: var(--pos-radius-md, 8px); min-width: 0; min-height: 200px; }
  .dhk8-resv-col.is-compact { min-height: 120px; }
  .dhk8-resv-col.is-today { border-color: var(--pos-tint, #1F8B3A); }
  .dhk8-resv-col__head { display: flex; justify-content: space-between; align-items: center; padding: 8px 10px; border-bottom: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.14)); cursor: pointer; font-family: var(--font-mono, ui-monospace); font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-resv-col.is-today .dhk8-resv-col__head { color: var(--pos-tint, #1F8B3A); font-weight: 700; }
  .dhk8-resv-col__count { padding: 1px 6px; background: var(--pos-surface, #161C18); border: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.14)); border-radius: var(--pos-radius-full, 999px); font-size: 10px; color: var(--pos-fg, #E8EFE9); }
  .dhk8-resv-col__list { display: flex; flex-direction: column; gap: 6px; padding: 6px; flex: 1; overflow-y: auto; }
  .dhk8-resv-col__empty { padding: 16px; text-align: center; color: var(--pos-fg-tertiary, #7E8C82); font-size: 12px; }
  .dhk8-resv-col__add { display: inline-flex; align-items: center; justify-content: center; gap: 4px; padding: 8px; background: transparent; border: 1px dashed var(--pos-separator-strong, rgba(255,255,255,0.14)); color: var(--pos-fg-tertiary, #7E8C82); font-family: inherit; font-size: 11px; cursor: pointer; border-radius: var(--pos-radius-md, 8px); transition: background 120ms ease, color 120ms ease; }
  .dhk8-resv-col__add:hover { background: var(--pos-surface, #161C18); color: var(--pos-fg, #E8EFE9); }

  /* ── Card ──────────────────────────────────────────────────────────────
     NintaiGO: nada de adornos laterales. El estado se comunica via el
     <Badge> de status + el tinte sutil de fondo (warn/mute), no con
     barritas decorativas border-left que el sistema prohíbe. */
  .dhk8-resv-card { display: flex; flex-direction: column; gap: 4px; padding: 8px 10px; background: var(--pos-surface, #161C18); border: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.14)); border-radius: var(--pos-radius-md, 8px); cursor: pointer; transition: background 120ms ease; }
  .dhk8-resv-card:hover { background: color-mix(in oklch, var(--pos-surface, #161C18) 80%, white); }
  .dhk8-resv-card.tone-warn { background: color-mix(in oklch, var(--pos-warning, #E0A23C) 12%, var(--pos-surface, #161C18)); border-color: var(--pos-warning, #E0A23C); }
  .dhk8-resv-card.tone-ok { opacity: 0.65; }
  .dhk8-resv-card.tone-mute { opacity: 0.5; }
  .dhk8-resv-card.is-compact { padding: 4px 6px; gap: 2px; font-size: 11px; }
  .dhk8-resv-card__head { display: flex; justify-content: space-between; align-items: baseline; }
  .dhk8-resv-card__time { font-family: var(--font-mono, ui-monospace); font-size: 14px; font-variant-numeric: tabular-nums; color: var(--pos-fg, #E8EFE9); }
  .dhk8-resv-card.is-compact .dhk8-resv-card__time { font-size: 11px; }
  .dhk8-resv-card__code { font-family: var(--font-mono, ui-monospace); font-size: 10px; font-weight: 600; padding: 1px 5px; background: var(--pos-tint-soft, rgba(31,139,58,0.12)); color: var(--pos-tint, #1F8B3A); border: 1px solid var(--pos-tint, #1F8B3A); border-radius: var(--pos-radius-full, 999px); }
  .dhk8-resv-card__body { display: flex; justify-content: space-between; align-items: center; gap: 6px; }
  .dhk8-resv-card__name { font-size: 13px; color: var(--pos-fg, #E8EFE9); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dhk8-resv-card__party { display: inline-flex; align-items: center; gap: 3px; font-size: 11px; color: var(--pos-fg-tertiary, #7E8C82); flex: 0 0 auto; }
  .dhk8-resv-card__pills { display: flex; flex-wrap: wrap; gap: 3px; }
  .dhk8-resv-card__pill { display: inline-flex; align-items: center; gap: 2px; padding: 1px 6px; font-size: 9px; background: var(--pos-elevated, #1E2520); border: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.14)); color: var(--pos-fg-secondary, #B6BFB8); border-radius: var(--pos-radius-full, 999px); }
  .dhk8-resv-card__allergies { display: inline-flex; align-items: center; gap: 3px; padding: 2px 6px; background: rgba(224,162,60,0.12); color: var(--pos-warning, #E0A23C); border: 1px dashed var(--pos-warning, #E0A23C); border-radius: var(--pos-radius-sm, 4px); font-size: 10px; }
  .dhk8-resv-card__footer { display: flex; gap: 4px; align-items: center; padding-top: 4px; border-top: 1px dashed var(--pos-separator, rgba(255,255,255,0.07)); }
  .dhk8-resv-card__badge { padding: 1px 6px; font-size: 9px; letter-spacing: 0.06em; text-transform: uppercase; border: 1px solid currentColor; border-radius: var(--pos-radius-full, 999px); margin-left: auto; }

  /* ── Dialog fields ──────────────────────────────────────────────────── */
  .dhk8-resv-field { display: flex; flex-direction: column; gap: 4px; font-size: 11px; color: var(--fg-secondary, #B6BFB8); margin-bottom: 10px; }
  .dhk8-resv-field span { display: inline-flex; align-items: center; gap: 3px; }
  .dhk8-resv-field-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
  .dhk8-resv-field-row:has(> :nth-child(2):last-child) { grid-template-columns: 1fr 1fr; }
  .dhk8-resv-checks { display: flex; gap: 16px; padding: 4px 0 10px; }
  /* Lane #165 — selector de mesa pre-asignada (chips). */
  .dhk8-resv-tables { display: flex; flex-wrap: wrap; gap: 6px; padding: 4px 0; max-height: 180px; overflow-y: auto; }
  .dhk8-resv-table {
    min-width: 56px; min-height: 52px; padding: 4px 8px;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
    background: var(--pos-surface, #161C18);
    color: var(--pos-fg, #E8EFE9);
    border: 2px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    font: inherit; cursor: pointer;
    border-radius: var(--pos-radius-md, 8px);
  }
  .dhk8-resv-table:hover { background: var(--pos-elevated, #1E2520); }
  .dhk8-resv-table.is-active { background: var(--pos-tint-soft, rgba(31,139,58,.15)); border-color: var(--pos-tint, #1F8B3A); color: var(--pos-tint-on, #48C76C); }
  .dhk8-resv-table-code { font-family: var(--font-mono, ui-monospace); font-size: 13px; font-weight: 700; }
  .dhk8-resv-table-cap { font-size: 10px; color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-resv-table.is-active .dhk8-resv-table-cap { color: inherit; opacity: 0.85; }
  .dhk8-resv-check { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; cursor: pointer; color: var(--fg, var(--pos-fg, #E8EFE9)); }
  .dhk8-resv-check input { width: 16px; height: 16px; }

  @media (max-width: 720px) {
    .dhk8-resv-grid { grid-template-columns: 1fr !important; }
    .dhk8-resv-field-row { grid-template-columns: 1fr !important; }
  }
`
